/**
 * SegmentedControl — PRIMEIRO componente autoral do GenUI 2 (não vem do CLI do
 * shadcn; `shadcn add` nunca vai sobrescrever este arquivo).
 *
 * Por que existe: o shadcn não tem segmented control e o `ToggleGroup` cru não
 * tem indicador deslizante. Mas o COMPORTAMENTO (valor único, roving tabindex,
 * navegação por seta, aria-pressed) vem de graça do `ToggleGroup` do Base UI —
 * então isto é só a camada visual: trilha, thumb animado e o eixo de badge.
 * Nada de acessibilidade reimplementada.
 *
 * Referência: SegmentedControl do @openai/apps-sdk-ui. De lá vieram três
 * decisões medidas no componente real: o thumb tem largura DINÂMICA (mede o
 * segmento ativo, em vez de fatiar a trilha em partes iguais — "Conversa" é
 * mais larga que "Viagem" e o indicador respeita isso); a curva é
 * `cubic-bezier(0.19, 1, 0.22, 1)` em 300ms (desaceleração longa, sem elástico);
 * e o thumb marca elevação por SOMBRA, não por borda — os dois juntos
 * duplicariam o marcador.
 *
 * É a receita única do controle no app: trilha sand-3 e thumb com 2px de respiro
 * em volta, em TODA variante e tamanho. As abas em pílula (`TabsList variant="pill"`)
 * repetem a mesma trilha e a mesma pílula: o que muda lá é só a semântica (aba).
 * Respiro de 1px com sombra que desce faz a pílula parecer mais baixa que a trilha.
 *
 * `fill` faz a trilha ocupar a largura toda com segmentos iguais (uso na
 * sidebar); sem ela, cada segmento tem a largura do próprio texto.
 *
 * `variant="glass"` é o alternador que pousa no gradiente da modal de lançamento
 * (#6291, Figma H12.1b `6837:20772`): trilho em vidro fumê (escurece, nunca some
 * no gradiente que vai a branco), lente em vidro claro, 48 de altura com o mesmo respiro de 2px, rótulo
 * `VOLL/subtitle` (16/22 medium) e ícone em TODO segmento. A opção inativa fica
 * em `foreground`, não em `muted-foreground` (3,6:1 sobre o trilho): a lente é o
 * sinal, como no segmented do iOS. Ignora `size` — o degrau é um só. A lente
 * desliza em 240ms `cubic-bezier(0.2,0,0,1)`; com `prefers-reduced-motion` ela
 * some e o segmento ativo veste o próprio vidro, com troca só de opacidade
 * (120ms) e nenhuma translação.
 */
import { cn } from '../lib/utils';
import { glassVariants } from './glass-label';
import { ToggleGroup, ToggleGroupItem } from './toggle-group';
import * as React from 'react';

/* O `translate-y-[0.8px]` é compensação óptica MEDIDA (2026-08-20): o Geist tem
   ascent > descent, e o glifo desenha 1.6px acima do centro geométrico do line
   box — em pill baixa isso lê como "texto colado no topo". O transform desce só
   a pintura; layout, indicador e foco não mudam. Fica fora da `CAIXA` porque o
   segmento que veste o próprio thumb (`segmentedRecipe`) desceria junto com o fundo. */
const CAIXA = {
  sm: 'h-7 px-3 text-[13px]',
  md: 'h-8 px-3.5 text-ui',
  lg: 'h-9 px-3.5 text-ui',
} as const;
const SIZES = {
  sm: { trilha: 'h-8 p-0.5', item: `${CAIXA.sm} translate-y-[0.8px]` },
  md: { trilha: 'h-9 p-0.5', item: `${CAIXA.md} translate-y-[0.8px]` },
  lg: { trilha: 'h-10 p-0.5', item: `${CAIXA.lg} translate-y-[0.8px]` },
} as const;

type Size = keyof typeof SIZES;

const THUMB = 'bg-card shadow-[0_1px_4px_-1px_rgba(33,32,28,0.22)]';

/**
 * A mesma receita para quem precisa de OUTRA semântica — `radiogroup`, em que a seta já
 * escolhe, contra os botões pressionados do `ToggleGroup` — sem copiar classes: trilha,
 * segmento e o segmento ativo vestindo o thumb (sem deslize, que é do componente).
 */
const segmentedRecipe = {
  thumb: THUMB,
  track: (size: Size) => `items-center rounded-full bg-sand-3 ${SIZES[size].trilha}`,
  segment: (size: Size) =>
    `cursor-pointer rounded-full font-medium whitespace-nowrap text-muted-foreground transition-colors duration-150 hover:text-foreground ${CAIXA[size]}`,
  active: `${THUMB} text-foreground hover:text-foreground`,
};

/* O vidro tem um degrau só (48): o `size` não se aplica. Sem a compensação óptica
   do `translate-y`: o frame centra a caixa de linha (22 em 44 → 11 de cada lado) e
   o print do Figma é o alvo. O `group-data-[spacing=0]` repete o padding e os raios
   porque o item do `ToggleGroup` já declara `px-2`, `rounded-none` e as pontas
   `rounded-l/r-lg` nessa variante, que venceriam os nossos: o anel de foco e a lente
   do movimento reduzido sairiam com canto. */
const GLASS = {
  trilha: 'h-12 p-0.5',
  item: 'h-11 cursor-pointer px-3.5 text-base leading-[1.375rem] text-foreground group-data-[spacing=0]/toggle-group:rounded-full group-data-horizontal/toggle-group:data-[spacing=0]:first:rounded-l-full group-data-horizontal/toggle-group:data-[spacing=0]:last:rounded-r-full group-data-[spacing=0]/toggle-group:px-3.5 motion-reduce:transition-[background-color,box-shadow] motion-reduce:duration-120 motion-reduce:aria-pressed:bg-card/96 motion-reduce:aria-pressed:shadow-[inset_0_1px_0_var(--card),0_0_0_1px_rgba(33,32,28,0.08),0_1px_2px_rgba(33,32,28,0.1),0_4px_16px_rgba(0,0,0,0.05)]',
  indicador: 'duration-240 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:hidden',
} as const;

type Variant = 'default' | 'glass';

type Thumb = { left: number; width: number };

const ITEM = '[data-slot="segmented-control-item"]';

/* Mede o segmento do valor ativo (o item carrega o próprio valor em `data-value`). */
function medirThumb(
  raiz: HTMLElement,
  value: string,
  setThumb: React.Dispatch<React.SetStateAction<Thumb | null>>,
) {
  const ativo = Array.from(raiz.querySelectorAll<HTMLElement>(ITEM)).find(
    (item) => item.dataset['value'] === value,
  );
  if (ativo) setThumb({ left: ativo.offsetLeft, width: ativo.offsetWidth });
}

const ItemContext = React.createContext<{
  size: Size;
  fill: boolean;
  variant: Variant;
}>({
  size: 'sm',
  fill: false,
  variant: 'default',
});

function SegmentedControl({
  value,
  onValueChange,
  size = 'sm',
  fill = false,
  variant = 'default',
  className,
  children,
  ...props
}: Omit<
  React.ComponentProps<typeof ToggleGroup>,
  'value' | 'onValueChange' | 'size' | 'variant'
> & {
  value: string;
  onValueChange: (value: string) => void;
  size?: Size;
  fill?: boolean;
  variant?: Variant;
}) {
  const trilha = React.useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = React.useState<Thumb | null>(null);
  const itemContextValue = React.useMemo(() => ({ size, fill, variant }), [size, fill, variant]);
  const glass = variant === 'glass';

  // Mede o segmento ativo. Roda a cada troca de valor e em resize da trilha (a sidebar
  // muda de largura no colapso) ou de um segmento (o rótulo ou o badge mudou).
  React.useLayoutEffect(() => {
    const raiz = trilha.current;
    if (!raiz) return undefined;
    const medir = () => {
      medirThumb(raiz, value, setThumb);
    };
    medir();
    const observer = new ResizeObserver(medir);
    observer.observe(raiz);
    for (const item of raiz.querySelectorAll(ITEM)) observer.observe(item);
    return () => {
      observer.disconnect();
    };
  }, [value]);

  return (
    <ToggleGroup
      ref={trilha}
      data-slot="segmented-control"
      value={[value]}
      onValueChange={(v: string[]) => {
        if (v[0]) onValueChange(v[0]);
      }}
      spacing={0}
      className={cn(
        'relative isolate flex items-center rounded-full',
        glass ? [glassVariants({ material: 'smoke' }), GLASS.trilha] : 'bg-sand-3',
        fill && 'w-full',
        !glass && SIZES[size].trilha,
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        data-slot="segmented-control-indicator"
        className={cn(
          THUMB,
          'pointer-events-none absolute top-0.5 left-0 rounded-full transition-[transform,width] duration-300 ease-[cubic-bezier(0.19,1,0.22,1)] motion-reduce:transition-none',
          glass && [glassVariants({ material: 'lens' }), GLASS.indicador],
        )}
        style={{
          height: `calc(100% - 0.25rem)`,
          width: thumb?.width ?? 0,
          transform: `translateX(${String(thumb?.left ?? 0)}px)`,
          opacity: thumb ? 1 : 0,
        }}
      />
      <ItemContext.Provider value={itemContextValue}>{children}</ItemContext.Provider>
    </ToggleGroup>
  );
}

function SegmentedControlItem({
  className,
  children,
  badge,
  ...props
}: React.ComponentProps<typeof ToggleGroupItem> & { badge?: React.ReactNode }) {
  const { size, fill, variant } = React.useContext(ItemContext);
  return (
    <ToggleGroupItem
      data-slot="segmented-control-item"
      data-value={props.value}
      className={cn(
        'text-muted-foreground z-10 gap-1.5 rounded-full font-medium transition-colors duration-150',
        // `aria-pressed:bg-transparent` desliga o `bg-muted` que o toggleVariants
        // aplica no ativo — ele cobria o thumb branco com sand-50.
        'aria-pressed:text-foreground aria-pressed:bg-transparent hover:text-foreground hover:bg-transparent',
        fill && 'flex-1',
        variant === 'glass' ? GLASS.item : SIZES[size].item,
        className,
      )}
      {...props}
    >
      {children}
      {badge == null ? null : (
        <span className="bg-(--sand-75) rounded-full px-1.5 text-[11px] tabular-nums">{badge}</span>
      )}
    </ToggleGroupItem>
  );
}

export { SegmentedControl, SegmentedControlItem, segmentedRecipe };
